import React, { useEffect, useState, useMemo } from 'react';
import { PublicKey } from '@solana/web3.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { WalletAdapterName } from '@/contexts/WalletContext';
import { shortenAddress } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useConnectWallet, usePostPrimaryWallet, useWalletList } from '@/hooks/useWalletQueries';
import IconSolflare from '@/assets/icons/solflare.jpeg';
import IconBackpack from '@/assets/icons/backpack.png';

interface InAppBrowserWalletMismatchModalProps {
  visible: boolean;
  requiredWalletType: WalletAdapterName | null;
  onClose: () => void;
  onConnect: (walletType: WalletAdapterName) => Promise<PublicKey>;
}

const InAppBrowserWalletMismatchModal: React.FC<InAppBrowserWalletMismatchModalProps> = ({
  visible,
  requiredWalletType,
  onClose,
  onConnect,
}) => {
  const [connecting, setConnecting] = useState(false);
  const [newlyConnectedWalletId, setNewlyConnectedWalletId] = useState<number | null>(null);
  const { toast } = useToast();

  const walletListQuery = useWalletList();
  const { mutateAsync: connectWalletAsync } = useConnectWallet();
  const { mutateAsync: setPrimaryWalletAsync, isPending: isLoadingPrimaryWallet } = usePostPrimaryWallet();
  const allWallets = walletListQuery.data?.wallets || [];

  // Filter wallets by required wallet type
  const filteredWallets = useMemo(() => {
    return allWallets.filter((w) => w.walletAdapter === requiredWalletType);
  }, [allWallets, requiredWalletType]);

  // Refetch wallets when modal opens
  useEffect(() => {
    if (visible && requiredWalletType) {
      walletListQuery.refetch();
    }
  }, [visible, requiredWalletType]);

  const handleSetPrimary = async (walletId: number) => {
    try {
      await setPrimaryWalletAsync(walletId);

      // Clear the "NEW" badge since wallet is now set as primary
      setNewlyConnectedWalletId(null);

      toast({
        title: 'Primary Wallet Set',
        description: 'Successfully set wallet as primary.',
      });

      // Close modal - the autoReconnectPrimaryWallet useEffect will trigger reconnection
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to set wallet as primary',
        variant: 'destructive',
      });
    }
  };

  const handleAddNewWallet = async () => {
    if (!requiredWalletType) return;

    // Store current wallet IDs before connecting
    const currentWalletIds = new Set(filteredWallets.map(w => w.id));

    setConnecting(true);
    try {
      // Step 1: Connect to wallet (returns PublicKey)
      const connectedPublicKey = await onConnect(requiredWalletType);

      // Step 2: Save wallet to database via API
      await connectWalletAsync({
        publicKey: connectedPublicKey.toString(),
        walletAdapter: requiredWalletType,
      });

      // Step 3: Refetch wallet list (React Query handles this automatically after mutation)
      await walletListQuery.refetch();

      // Find the newly connected wallet (not in current list)
      const newWallet = allWallets
        .filter((w) => w.walletAdapter === requiredWalletType)
        .find(w => !currentWalletIds.has(w.id));

      if (newWallet) {
        // Set the newly connected wallet ID to show badge
        setNewlyConnectedWalletId(newWallet.id);

        toast({
          title: 'Wallet Connected',
          description: 'New wallet added to the list. You can now set it as primary.',
        });
      }
    } catch (error: any) {
      // Don't show error for redirecting (user is being redirected to wallet app)
      if (!error.message?.includes('Redirecting')) {
        toast({
          title: 'Connection Failed',
          description: error.message || 'Failed to connect wallet',
          variant: 'destructive',
        });
      }
    } finally {
      setConnecting(false);
    }
  };

  // Sort wallets: newly connected first, then others
  // IMPORTANT: This must be BEFORE the early return to avoid hook order violations
  const sortedWallets = useMemo(() => {
    if (newlyConnectedWalletId) {
      const newWallet = filteredWallets.find(w => w.id === newlyConnectedWalletId);
      const others = filteredWallets.filter(w => w.id !== newlyConnectedWalletId);
      return newWallet ? [newWallet, ...others] : filteredWallets;
    }
    return filteredWallets;
  }, [filteredWallets, newlyConnectedWalletId]);

  const getWalletIcon = (walletType: WalletAdapterName) => {
    switch (walletType) {
      case 'Solflare':
        return IconSolflare;
      case 'Backpack':
        return IconBackpack;
      default:
        return null;
    }
  };

  if (!requiredWalletType) return null;

  const walletIcon = getWalletIcon(requiredWalletType);
  const loading = walletListQuery.isLoading;

  return (
    <Dialog open={visible} onOpenChange={() => {/* Blocking - no close */}}>
      <DialogContent
        className="sm:max-w-[500px] bg-backgroundGray border-[#2A2A2A]"
        shouldShowCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle className="text-white text-xl font-inter flex items-center gap-3">
            {walletIcon && (
              <img src={walletIcon} alt={requiredWalletType} className="w-6 h-6 rounded-lg" />
            )}
            {requiredWalletType} In-App Browser Detected
          </DialogTitle>
          <DialogDescription className="text-tertiary font-inter">
            You're using {requiredWalletType} in-app browser, but your primary wallet is a different type.
            Please select a {requiredWalletType} wallet to set as primary, or add a new one.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-tertiary font-inter">Loading wallets...</div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 mt-4">
            {/* List of existing wallets */}
            {sortedWallets.length > 0 ? (
              <div className="flex flex-col gap-2">
                <p className="text-sm text-[#888] font-inter mb-1">
                  Your {requiredWalletType} wallets:
                </p>
                {sortedWallets.map((wallet) => (
                  <div
                    key={wallet.id}
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-black border border-[#2A2A2A]"
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-inter font-medium">
                          {shortenAddress(wallet.publicKey)}
                        </span>
                        {wallet.id === newlyConnectedWalletId && (
                          <span className="px-2 py-0.5 text-xs font-inter font-semibold text-white bg-red-500 rounded">
                            NEW
                          </span>
                        )}
                      </div>
                      {wallet.balanceSol !== undefined && (
                        <span className="text-xs text-[#888] font-inter">
                          {wallet.balanceSol.toFixed(4)} SOL
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleSetPrimary(wallet.id)}
                      disabled={isLoadingPrimaryWallet}
                      className="px-4 py-2 rounded-lg bg-black hover:bg-[#1A1A1A] border border-[#2A2A2A] text-white font-inter text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoadingPrimaryWallet ? 'Setting...' : 'Set as Primary'}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center">
                <p className="text-tertiary font-inter text-sm">
                  No {requiredWalletType} wallets found.
                </p>
                <p className="text-tertiary font-inter text-xs mt-1">
                  Add a new wallet below to continue.
                </p>
              </div>
            )}

            {/* Add New Wallet Button */}
            <div className="pt-2 border-t border-[#2A2A2A]">
              <button
                onClick={handleAddNewWallet}
                disabled={connecting}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-black hover:bg-[#1A1A1A] transition-colors border border-[#2A2A2A] text-white font-inter font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {connecting ? (
                  <>Connecting...</>
                ) : (
                  <>
                    <span className="text-xl">+</span>
                    Add New {requiredWalletType} Wallet
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InAppBrowserWalletMismatchModal;
